/**
 * dailyQuote — one quote per day for the sidebar's running text (Mikko,
 * 2026-10-12: "a quote is a good one per day").
 *
 * The quote is chosen by the LOCAL calendar date alone, so everybody sees the
 * same one on the same day, it changes at local midnight, and nothing is stored
 * or fetched — no list, no network, nothing that can fail. The built-in list
 * can be replaced from Home Settings (one quote per line).
 *
 * Import-free, so it is testable without the app's env vars.
 */
export const DAILY_QUOTES: readonly string[] = [
  "Quality is not an act, it is a habit. — Will Durant",
  "Well done is better than well said. — Benjamin Franklin",
  "The secret of getting ahead is getting started. — Mark Twain",
  "Do the best you can until you know better. Then when you know better, do better. — Maya Angelou",
  "It always seems impossible until it's done. — Nelson Mandela",
  "Coming together is a beginning; keeping together is progress; working together is success. — Henry Ford",
  "Whatever you are, be a good one. — Abraham Lincoln",
  "Quality means doing it right when no one is looking. — Henry Ford",
  "Small things done with great care make great things.",
  "The best way to predict the future is to create it. — Peter Drucker",
  "Not everything that counts can be counted. — William Bruce Cameron",
  "Action is the foundational key to all success. — Pablo Picasso",
  "Success is the sum of small efforts, repeated day in and day out. — Robert Collier",
  "Talent wins games, but teamwork and intelligence win championships. — Michael Jordan",
  "Alone we can do so little; together we can do so much. — Helen Keller",
  "Perfection is not attainable, but if we chase perfection we can catch excellence. — Vince Lombardi",
  "Excellence is doing ordinary things extraordinarily well. — John W. Gardner",
  "The way to get started is to quit talking and begin doing. — Walt Disney",
  "Don't watch the clock; do what it does. Keep going. — Sam Levenson",
  "A job well done is its own reward.",
  "Measure twice, cut once.",
  "A stitch in time saves nine.",
  "Many hands make light work.",
  "Slow is smooth, smooth is fast.",
  "Rome wasn't built in a day.",
  "The harder I work, the luckier I get. — Samuel Goldwyn",
  "You miss 100% of the shots you don't take. — Wayne Gretzky",
  "Start where you are. Use what you have. Do what you can. — Arthur Ashe",
  "Courage is not the absence of fear, but the triumph over it. — Nelson Mandela",
  "Strive not to be a success, but rather to be of value. — Albert Einstein",
  "Continuous improvement is better than delayed perfection. — Mark Twain",
  "What gets measured gets managed. — Peter Drucker",
  "Teamwork makes the dream work. — John C. Maxwell",
  "If you want to go fast, go alone. If you want to go far, go together.",
  "Great things are done by a series of small things brought together. — Vincent van Gogh",
  "The only way to do great work is to love what you do. — Steve Jobs",
  "Fall seven times, stand up eight. — Japanese proverb",
  "A journey of a thousand miles begins with a single step. — Lao Tzu",
  "Energy and persistence conquer all things. — Benjamin Franklin",
  "Don't be afraid to give up the good to go for the great. — John D. Rockefeller",
  "Opportunities are usually disguised as hard work, so most people don't recognize them. — Ann Landers",
  "Quality is everyone's responsibility. — W. Edwards Deming",
  "In God we trust; all others must bring data. — W. Edwards Deming",
  "Make each day your masterpiece. — John Wooden",
  "Never leave till tomorrow which you can do today. — Benjamin Franklin",
  "Patience and perseverance have a magical effect before which difficulties disappear. — John Quincy Adams",
  "The expert in anything was once a beginner. — Helen Hayes",
  "Dream big. Start small. Act now.",
  "Good, better, best. Never let it rest, until your good is better and your better is best.",
  "Today's effort is tomorrow's result.",
  "Take pride in how far you have come, and have faith in how far you can go.",
  "Diligence is the mother of good luck. — Benjamin Franklin",
  "Hard work beats talent when talent doesn't work hard. — Tim Notke",
  "A smooth sea never made a skilled sailor. — Franklin D. Roosevelt",
  "Kung may tiyaga, may nilaga.",
];

/** Whole local days since 1970-01-01 — the same number all day, anywhere in the building. */
export function localDayNumber(d: Date): number {
  return Math.floor(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 86_400_000);
}

/** One quote per line; blank lines and surrounding space dropped. */
export function parseCustomQuotes(text: string | undefined | null): string[] {
  return (text ?? "").split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
}

/** Today's quote: from `custom` when it has any, else the built-in list. */
export function quoteForDate(d: Date, custom?: readonly string[]): string {
  const list = custom && custom.length > 0 ? custom : DAILY_QUOTES;
  return list[localDayNumber(d) % list.length];
}

/**
 * What the sidebar runs. Quote mode wins over the typed text; with it off the
 * typed text runs, and blank means no ticker (the version stamp shows).
 */
export function tickerMessage(
  t: { text?: string; quotes?: boolean; customQuotes?: string },
  d: Date,
): string {
  if (t.quotes) return quoteForDate(d, parseCustomQuotes(t.customQuotes));
  return (t.text ?? "").trim();
}
